import { Component, EventEmitter, Inject, OnInit, Output } from '@angular/core';
import { Store } from '@ngxs/store';
import { Observable } from 'rxjs';
import { ContactState } from 'app/store/contact/contact.state';
import { BorrowerState } from 'app/store/borrower/borrower/borrower.state';
import { GetContactsForBorrower } from 'app/store/contact/contact.actions';
import { DocumentCategory } from 'app/models/dto/document-category.model';
import { DocumentState } from 'app/store/documents/document.state';
import { GetDocumentCategories } from 'app/store/documents/document.actions';
import { PatchDealDocument } from 'app/store/deal/deal-documents/deal-document.actions';
import { Document } from 'app/models/dto/document.model';
import { Contact } from 'app/models/dto/contact.model';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { DOCUMENT_CATEGORIES_REQUIRING_YEAR_STRING, LAST_24_MONTHS_SELECT_OPTIONS } from 'app/documents/document-upload-dialog/document-upload-constants';
import { CommonModule } from '@angular/common';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';

@Component({
  selector: 'app-advanced-document-edit-dialog',
  templateUrl: './advanced-document-edit-dialog.component.html',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatIconModule
  ]
})
export class AdvancedDocumentEditDialogComponent implements OnInit {
  @Output() handleSave = new EventEmitter<void>();
  @Output() handleCancel = new EventEmitter<void>();
  categories$: Observable<DocumentCategory[]> = this.store.select(DocumentState.documentCategories);
  contacts$ =this.store.select(ContactState.contacts);

  editDocumentForm: FormGroup;
  years: string[] = [];
  categoriesRequiringYear: string[] = DOCUMENT_CATEGORIES_REQUIRING_YEAR_STRING;
  last24Months: { display: string, value: string }[] = LAST_24_MONTHS_SELECT_OPTIONS;

  /**
   * Inject data from the client via `data`.
   */
  constructor(
    private store: Store,
    // @Inject(MAT_DIALOG_DATA) public data: { borrowerId: number, contact: Contact, formMode: string },
    @Inject(MAT_DIALOG_DATA) public data: {
      // Dialog data.
      document: Document;
      dealId: number;
    },
    public selfDialogRef: MatDialogRef<AdvancedDocumentEditDialogComponent>,
  ) {
    this.years = Array.from({ length: 10 }, (v, i) => {
      const year = new Date().getFullYear() - i;
      return year.toString();
    });
  }

  ngOnInit(): void {
    if (this.data.document) {
      this.store.dispatch(new GetDocumentCategories());
      this.store.dispatch(new GetContactsForBorrower(this.data.document.borrowerId));
    }

    // Get primary contact as default if no contact is assigned
    const primaryContact = this.store.selectSnapshot(BorrowerState.borrower)?.primary_contact;
    const defaultContact = this.data.document.contact || primaryContact;

    this.editDocumentForm = new FormGroup({
      title: new FormControl(this.data.document.title),
      category: new FormControl(this.data.document.category),
      monthsString: new FormControl(this.safeMonthsString()),
      yearsString: new FormControl(this.safeYearsString()),
      contact: new FormControl(defaultContact)
    });

  }

  save(form: FormGroup) {
    // Make a copy of the current document.
    const doc: Document = {...this.data.document};
    doc.title = form.value.title;
    doc.category = form.value.category;
    doc.contact = form.value.contact;
    doc.contactId = form.value.contact.id;
    if (form.value.monthsString) {
      // First, check the category. Only applies to bankStatement.
      if (doc.category !== 'bankStatement') {
        doc.monthsString = undefined;
      } else {
        // Split by the / and ensure it's a valid month/year.
        const mthStr = form.value.monthsString.split('/');
        if (mthStr[0] > 12 || mthStr[0] < 1) {
          form.controls.monthsString.setErrors({'invalid-month': true });
          return;
        }
        // Get current year. Docs should always be less than current year.
        const year = new Date().getFullYear();
        if (mthStr[1] > year || mthStr[1] < (year - 2)) {
          form.controls.monthsString.setErrors({'invalid-year': true });
          return;
        }
        doc.monthsString = form.value.monthsString;
      }
    }

    if (form.value.yearsString) {
        const yrStr = form.value.yearsString;

        if (doc.category.match('TaxReturns')) {
          // Get current year. Docs should always be less than current year.
          const year = new Date().getFullYear();
          if (year <= yrStr) {
            form.controls.yearsString.setErrors({'invalid-year': true });
            return;
          }
        }

        doc.yearsString = form.value.yearsString;
    }

    const action = new PatchDealDocument(this.data.dealId, doc);
    this.store.dispatch(action).subscribe({
      next: () => {
        this.selfDialogRef.close();
        // Let the parent component know when this succeeds.
        this.handleSave.emit();
        this.selfDialogRef.close();
      },
    });
  }

  cancel($event: Event) {
    $event.stopPropagation();
    this.selfDialogRef.close();
  }

  safeMonthsString() {
    return this.data.document?.months ? this.data.document.months[0] : '';
  }

  safeYearsString() {
    return this.data.document?.years ? this.data.document.years[0] : '';
  }

  compareContacts(contact1: Contact, contact2: Contact): boolean {
    return contact1 && contact2 ? contact1.id === contact2.id : contact1 === contact2;
  }
}
